/**
 * Cron expressions, in English.
 *
 * The sync cadence is configured as cron because that is what the scheduler
 * speaks, but `45 3 * * *` is not something anyone should have to decode on a
 * status screen — the question being asked there is "when does this next run",
 * and the answer is a sentence.
 *
 * Deliberately partial: this phrases the shapes the sync config actually uses
 * (a time of day, an interval, a weekday or day of month) and returns `null`
 * for anything it cannot say plainly. A caller that gets `null` shows the raw
 * expression, which is honest — an approximate description of when a job runs
 * would be worse than no description, because it would be believed.
 */
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
/**
 * Turns a 5-field cron expression into a sentence, or returns null when it
 * cannot be said plainly.
 *
 * The result is a fragment meant to stand alone in a table cell — "Daily at
 * 3:45 AM", "Every hour, on the hour", "Sundays at 5:00 AM" — with no trailing
 * punctuation and no timezone. The timezone belongs to the caller: cron is
 * evaluated in whatever zone the scheduler was given, and this function is not
 * told which one.
 */
export function describeCron(expression) {
    const fields = expression.trim().split(/\s+/);
    if (fields.length !== 5)
        return null;
    const [minuteField, hourField, dayOfMonthField, monthField, dayOfWeekField] = fields;
    if (!minuteField || !hourField || !dayOfMonthField || !monthField || !dayOfWeekField)
        return null;
    const minute = parseField(minuteField, 0, 59);
    const hour = parseField(hourField, 0, 23);
    const dayOfMonth = parseField(dayOfMonthField, 1, 31);
    const month = parseField(monthField, 1, 12);
    /** 0 and 7 both mean Sunday, which is why this field runs to 7. */
    const dayOfWeek = parseField(dayOfWeekField, 0, 7);
    if (!minute || !hour || !dayOfMonth || !month || !dayOfWeek)
        return null;
    /** A month restriction is rare enough that phrasing it is not worth the risk. */
    if (month.kind !== 'any')
        return null;
    const days = describeDays(dayOfMonth, dayOfWeek);
    if (days === null)
        return null;
    /**
     * No fixed hour means this fires repeatedly through the day, which is a
     * frequency ("every 15 minutes") rather than a time ("at 3:15 AM"). The two
     * read differently enough that they are built separately.
     */
    if (hour.kind !== 'values') {
        const frequency = describeFrequency(minute, hour);
        if (frequency === null)
            return null;
        return days === 'Daily'
            ? capitalise(frequency)
            : `${days}, ${frequency}`;
    }
    if (minute.kind !== 'values')
        return null;
    const times = hour.values.flatMap((h) => minute.values.map((m) => formatTime(h, m)));
    if (times.length === 0 || times.length > 4)
        return null;
    return `${days} at ${list(times)}`;
}
/**
 * Which days this runs on: "Daily", a weekday phrase, or a day-of-month phrase.
 *
 * Restricting both day-of-month and day-of-week is refused rather than
 * described. Cron treats the pair as an OR — `0 0 1 * 1` is the 1st *or* any
 * Monday, not the 1st when it falls on a Monday — and a sentence that reads
 * naturally would almost certainly be read as the wrong one of those.
 */
function describeDays(dayOfMonth, dayOfWeek) {
    const byMonth = dayOfMonth.kind !== 'any';
    const byWeek = dayOfWeek.kind !== 'any';
    if (byMonth && byWeek)
        return null;
    if (!byMonth && !byWeek)
        return 'Daily';
    if (byWeek) {
        if (dayOfWeek.kind !== 'values')
            return null;
        /** Cron accepts 7 for Sunday as well as 0; both name the same day. */
        const days = [...new Set(dayOfWeek.values.map((value) => value % 7))].sort();
        if (days.length === 5 && days.join() === '1,2,3,4,5')
            return 'Weekdays';
        if (days.length === 2 && days.join() === '0,6')
            return 'Weekends';
        if (days.length === 7)
            return 'Daily';
        return list(days.map((day) => `${DAY_NAMES[day]}s`));
    }
    if (dayOfMonth.kind !== 'values')
        return null;
    if (dayOfMonth.values.length > 3)
        return null;
    return `The ${list(dayOfMonth.values.map(ordinal))} of each month`;
}
/**
 * How often this fires when there is no single time of day to name.
 *
 * An unrestricted or stepped hour field means this fires more than once a day,
 * and the minute field then decides whether the cadence is hourly or finer.
 */
function describeFrequency(minute, hour) {
    const everyHour = hour.kind === 'any';
    const hourStep = hour.kind === 'step' ? hour.step : 1;
    // A fixed minute past every hour (or every nth hour) — the common case.
    if (minute.kind === 'values' && minute.values.length === 1) {
        const [past] = minute.values;
        const cadence = everyHour ? 'every hour' : `every ${hourStep} hours`;
        return past === 0 ? `${cadence}, on the hour` : `${cadence} at :${pad(past)}`;
    }
    // Sub-hourly cadences only make sense against an unrestricted hour field.
    if (!everyHour)
        return null;
    if (minute.kind === 'any')
        return 'every minute';
    if (minute.kind === 'step') {
        return minute.step === 1 ? 'every minute' : `every ${minute.step} minutes`;
    }
    /**
     * An explicit minute list is a cadence only when it covers the hour evenly
     * from the top — `0,30` is "every 30 minutes", but `0,7,44` has no honest
     * phrasing, and neither does `10,40`, which is every 30 minutes offset by ten
     * and would be read as on the half hour.
     */
    const values = minute.values;
    const [first, gap] = values;
    if (first !== 0 || gap === undefined)
        return null;
    const even = 60 % gap === 0 &&
        values.length === 60 / gap &&
        values.every((value, index) => value === index * gap);
    return even ? `every ${gap} minutes` : null;
}
/** Parses one cron field, or null if it is not a shape this module handles. */
function parseField(field, min, max) {
    if (field === '*')
        return { kind: 'any' };
    const step = /^\*\/(\d+)$/.exec(field);
    if (step) {
        const size = Number(step[1]);
        return size >= 1 && size <= max ? { kind: 'step', step: size } : null;
    }
    const values = new Set();
    for (const part of field.split(',')) {
        const range = /^(\d+)-(\d+)$/.exec(part);
        if (range) {
            const from = Number(range[1]);
            const to = Number(range[2]);
            if (from < min || to > max || from > to)
                return null;
            for (let value = from; value <= to; value += 1)
                values.add(value);
            continue;
        }
        if (!/^\d+$/.test(part))
            return null;
        const value = Number(part);
        if (value < min || value > max)
            return null;
        values.add(value);
    }
    const [first, ...rest] = [...values].sort((a, b) => a - b);
    return first === undefined ? null : { kind: 'values', values: [first, ...rest] };
}
/** 12-hour clock, because the firm's schedules are read by people, not cron. */
function formatTime(hour, minute) {
    const suffix = hour < 12 ? 'AM' : 'PM';
    const twelve = hour % 12 === 0 ? 12 : hour % 12;
    return `${twelve}:${pad(minute)} ${suffix}`;
}
function ordinal(value) {
    const teen = value % 100;
    if (teen >= 11 && teen <= 13)
        return `${value}th`;
    switch (value % 10) {
        case 1:
            return `${value}st`;
        case 2:
            return `${value}nd`;
        case 3:
            return `${value}rd`;
        default:
            return `${value}th`;
    }
}
function list(items) {
    if (items.length <= 1)
        return items[0] ?? '';
    if (items.length === 2)
        return `${items[0]} and ${items[1]}`;
    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}
function pad(value) {
    return String(value).padStart(2, '0');
}
function capitalise(value) {
    return value.charAt(0).toUpperCase() + value.slice(1);
}
//# sourceMappingURL=cron.js.map